import React, { useState, useRef, useContext } from 'react';
import { X, Image, Video, Type } from 'lucide-react';
import AppContext from '../../contexts/AppContext';
import * as firestoreService from '../../services/firestoreService';
import * as storageService from '../../services/storageService';
import SecurityUtils from '../../utils/security';

const SWING_GRADIENTS = [
  { id: 'teal',    cls: 'from-[#0f766e] to-[#134e4a]' },
  { id: 'green',   cls: 'from-[#16a34a] to-[#065f46]' },
  { id: 'navy',    cls: 'from-[#1e3a5f] to-[#0f766e]' },
  { id: 'amber',   cls: 'from-[#78350f] to-[#451a03]' },
  { id: 'slate',   cls: 'from-[#1c1917] to-[#166534]' },
  { id: 'ocean',   cls: 'from-[#0c4a6e] to-[#134e4a]' },
];

const SwingCreateModal = ({ onClose }) => {
  const { currentUser, setSwings } = useContext(AppContext);

  const [tab, setTab] = useState('photo'); // 'photo' | 'video' | 'text'
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [text, setText] = useState('');
  const [gradient, setGradient] = useState(SWING_GRADIENTS[0].cls);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const validation = SecurityUtils.validateFile(f, tab === 'video' ? 'video' : 'image');
    if (!validation.valid) { setError(validation.error); return; }
    setError('');
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleShare = async () => {
    if (uploading) return;
    if (tab === 'text' && !text.trim()) { setError('Please write something.'); return; }
    if ((tab === 'photo' || tab === 'video') && !file) { setError('Please select a file.'); return; }

    setUploading(true);
    try {
      let swingData = { userId: currentUser.id };
      if (tab === 'text') {
        swingData = { ...swingData, type: 'text', text: SecurityUtils.sanitizeText(text.trim()), bgGradient: gradient };
      } else {
        const { url, type } = await storageService.uploadSwingMedia(file, currentUser.id);
        swingData = { ...swingData, type, url };
      }
      const created = await firestoreService.createSwing(swingData);
      setSwings(prev => [created, ...prev]);
      onClose();
    } catch (err) {
      console.error('Error creating swing:', err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const tabs = [
    { id: 'photo', label: 'Photo', icon: Image },
    { id: 'video', label: 'Video', icon: Video },
    { id: 'text',  label: 'Text',  icon: Type  },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-gray-950 border border-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-800">
          <h2 className="text-base font-bold text-white">New Swing</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Type tabs */}
        <div className="flex border-b border-gray-800">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => { setTab(id); setFile(null); setPreview(null); setError(''); }}
              className={`flex-1 flex items-center justify-center space-x-1.5 py-3 text-sm font-medium transition-colors ${
                tab === id ? 'text-[#16a34a] border-b-2 border-[#16a34a]' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Content area */}
        <div className="p-5 space-y-4">

          {/* Photo / Video picker */}
          {(tab === 'photo' || tab === 'video') && (
            <>
              <input
                ref={tab === 'photo' ? photoInputRef : videoInputRef}
                type="file"
                accept={tab === 'photo' ? 'image/*' : 'video/*'}
                className="hidden"
                onChange={handleFileChange}
              />
              {preview ? (
                <div className="relative rounded-xl overflow-hidden bg-black">
                  {tab === 'photo' ? (
                    <img src={preview} alt="" className="w-full max-h-64 object-contain" />
                  ) : (
                    <video src={preview} className="w-full max-h-64 object-contain" controls playsInline />
                  )}
                  <button
                    onClick={() => { setFile(null); setPreview(null); }}
                    className="absolute top-2 right-2 bg-black/60 rounded-full p-1 text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => (tab === 'photo' ? photoInputRef : videoInputRef).current?.click()}
                  className="w-full h-36 border-2 border-dashed border-gray-700 rounded-xl flex flex-col items-center justify-center text-gray-500 hover:border-[#16a34a] hover:text-[#16a34a] transition-colors"
                >
                  {tab === 'photo' ? <Image className="w-8 h-8 mb-2" /> : <Video className="w-8 h-8 mb-2" />}
                  <span className="text-sm">Tap to select {tab}</span>
                </button>
              )}
            </>
          )}

          {/* Text composer */}
          {tab === 'text' && (
            <>
              {/* Live preview */}
              <div className={`w-full h-40 rounded-xl flex items-center justify-center bg-gradient-to-br ${gradient}`}>
                <p className="text-pure-white text-lg font-bold text-center px-6 leading-snug break-words">
                  {text || <span className="opacity-40">Your text…</span>}
                </p>
              </div>

              {/* Text input */}
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                maxLength={200}
                rows={3}
                placeholder="What's on your mind?"
                className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-xl text-white text-sm placeholder-gray-500 resize-none outline-none focus:ring-2 focus:ring-[#16a34a]"
              />

              {/* Gradient picker */}
              <div className="flex space-x-2.5">
                {SWING_GRADIENTS.map(g => (
                  <button
                    key={g.id}
                    onClick={() => setGradient(g.cls)}
                    className={`w-8 h-8 rounded-full bg-gradient-to-br ${g.cls} flex-shrink-0 transition-transform ${
                      gradient === g.cls ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-950 scale-110' : ''
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {error && <p className="text-sm text-red-400">{error}</p>}

          <button
            onClick={handleShare}
            disabled={uploading}
            className="w-full py-3 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 text-pure-white font-bold rounded-xl transition-colors"
          >
            {uploading ? 'Uploading…' : 'Share Swing'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SwingCreateModal;
